/**
 * =============================================================================
 * HELPERS - UTILITY FUNCTIONS FOR AUTOMATIONS FEATURE
 * =============================================================================
 * 
 * PURPOSE:
 * Contains helper functions used across the Automations feature.
 * Currently has one function for getting config panel header metadata.
 * 
 * USAGE:
 * These helpers map step types to their visual configuration.
 * Used by ConfigPanel to display appropriate header styling.
 * 
 * POTENTIAL EXPANSION FOR DRAG-AND-DROP:
 * Could add helper functions like:
 * - getDefaultStepData(type) - returns default step object for new steps
 * - generateStepId(type) - generates unique IDs for new steps
 * - validateStepOrder(steps) - ensures trigger is first
 * =============================================================================
 */
import { MessageSquare, Mail, Clock, Play, Zap } from 'lucide-react';

/**
 * Get configuration panel header metadata based on step type.
 * 
 * Used by ConfigPanel to display appropriate icon, title, and colors
 * when editing a workflow step.
 * 
 * @param {string} configType - The type of step being configured
 * @returns {Object} Header metadata with icon, title, bgColor, iconColor
 * 
 * RETURN SCHEMA:
 * {
 *   icon: LucideIcon,    // Icon component to display
 *   title: string,       // Panel header title
 *   bgColor: string,     // Tailwind background color class
 *   iconColor: string,   // Tailwind text color class for icon
 * }
 * 
 * STEP TYPE MAPPING:
 * - 'sms' -> MessageSquare icon, "Configure SMS"
 * - 'email' -> Mail icon, "Configure Email"
 * - 'wait' -> Clock icon (orange), "Configure Delay"
 * - 'trigger' -> Play icon, "Start Trigger"
 * - default -> Zap icon, "Configure"
 */
export const getConfigHeader = (configType) => {
  switch (configType) {
    case 'sms':
      return { icon: MessageSquare, title: 'Configure SMS', bgColor: 'bg-stone-100', iconColor: 'text-stone-600' };
    case 'email':
      return { icon: Mail, title: 'Configure Email', bgColor: 'bg-stone-100', iconColor: 'text-stone-600' };
    case 'wait':
      return { icon: Clock, title: 'Configure Delay', bgColor: 'bg-orange-50', iconColor: 'text-orange-500' };
    case 'trigger':
      return { icon: Play, title: 'Start Trigger', bgColor: 'bg-stone-100', iconColor: 'text-stone-600' };
    default:
      return { icon: Zap, title: 'Configure', bgColor: 'bg-stone-100', iconColor: 'text-stone-600' };
  }
};

/**
 * Insert a token into an input/textarea at the current cursor position.
 * Returns new value and cursor position.
 */
export const insertAtCursor = (inputRef, value, token) => {
  let start = 0;
  let end = 0;
  if (inputRef && inputRef.selectionStart !== undefined) {
    start = inputRef.selectionStart;
    end = inputRef.selectionEnd || start;
  } else {
    // No cursor info - append
    start = value.length;
    end = value.length;
  }

  const newValue = value.slice(0, start) + token + value.slice(end);
  const cursor = start + token.length;
  return { value: newValue, cursor };
};
